/**
 * Process-wide registry mapping step `type` strings to handler
 * implementations.
 *
 * The registry is intentionally a module-level singleton: every step
 * handler is registered via a single side-effect import (see
 * `handlers/index.ts`). This lets target apps tree-shake unused
 * handlers if they replace the barrel with explicit imports.
 *
 * Note on typing:
 * Handlers are contextually typed via this module's `StepHandler` type.
 * Without the contextual typing, `strict` TypeScript would infer handler
 * parameters as `any` which breaks compilation.
 */

import type { FlowStep } from "../types/flow";

import type { ExecutionContext } from "../context/execution-context";
import type { StepResult } from "./types";

/**
 * Step handler signature. Handlers may be sync or async.
 * - Returning `void` is treated as "ran successfully" without
 * special runner semantics.
 * - Returning a `StepResult` may request `f:return` / `err`-branch
 * execution when the runner dispatches it.
 */
export type StepHandler = (
 step: FlowStep,
 ctx: ExecutionContext,
) => Promise<StepResult | void> | StepResult | void;

const handlers = new Map<string, StepHandler>();

/**
 * Register (or replace) the handler for a given step type.
 */
export function registerStepHandler(type: string, handler: StepHandler): void {
 handlers.set(type, handler);
}

/**
 * Look up a handler by step type.
 * Returns `undefined` when none is registered so the runner can decide
 * whether to skip or fail.
 */
export function getStepHandler(type: string): StepHandler | undefined {
 return handlers.get(type);
}

/**
 * Reports whether a handler is currently registered.
 */
export function hasStepHandler(type: string): boolean {
 return handlers.has(type);
}

/**
 * Snapshot of all registered step types — useful for diagnostics and
 * developer tooling.
 */
export function listStepHandlers(): readonly string[] {
 return [...handlers.keys()].sort();
}